/** The tour record's section registry (plan §1–§2): every catalog key → its section on
 *  the Tour tab (or the Itinerary tab), the field group inside it, its block (Editable,
 *  the muted "From <import>" vendor block, the System block) and its position.
 *
 *  Sections come in registry order, Launch & mapping first. Launch & mapping renders its
 *  groups as blocks of its own (`LaunchMappingPanel`: the identifiers and tag-categories
 *  tables place their rows' fields in columns); the other sections lay their groups out
 *  as field grids (`TourSectionPanel`).
 *
 *  Contract (`registry.test.ts`): every catalog key is placed exactly once — or rides on
 *  another key's slot (`via`: the longitude is edited by the coordinates slot). */
import type { TourDetail } from '@/api/tours';
import type { FieldBlockKind } from '@/features/records/views/types';

import {
  ALIAS_FIELDS_CATALOG,
  CONTEXT_GROUPS,
  contextPath,
  ITINERARY_FIELDS_CATALOG,
  PRECHECK_FIELDS_CATALOG,
  PRETRIP_FIELDS_CATALOG,
  TAGCAT_FIELDS_CATALOG,
  WAIVER_CONTEXT_KEYS,
} from './catalog';

export type TourSectionId = 'launch' | 'basics' | 'schedule' | 'content' | 'guest' | 'itinerary';

export interface TourRegistryEntry {
  section: TourSectionId;
  group: string;
  block: FieldBlockKind;
  order: number;
  /** Edited by another key's slot (the coordinates slot also writes the longitude). */
  via?: string;
  /** Shown only for these tours (a fixed-date tour's start date, a recurring tour's
   *  departures) — decided by the stored tour, so the same in both modes. */
  when?: (detail: Pick<TourDetail, 'is_bespoke' | 'trip_type'>) => boolean;
}

export interface TourGroupDef {
  id: string;
  title?: string;
}

export interface TourSectionDef {
  id: TourSectionId;
  title: string;
  /** The help line under the section title. */
  note?: string;
  groups: TourGroupDef[];
}

/** The Tour tab's sections, in order (the Itinerary tab renders `itinerary`). */
export const TOUR_SECTIONS: TourSectionDef[] = [
  {
    id: 'launch',
    title: 'Launch & mapping',
    groups: [
      { id: 'mapping', title: 'External IDs' },
      { id: 'policy', title: 'Launch policy' },
      { id: 'access', title: 'Access and system tags' },
      { id: 'tag-categories', title: 'Tag categories' },
    ],
  },
  {
    id: 'basics',
    title: 'Basics',
    groups: [
      { id: 'basics' },
      { id: 'meeting', title: 'Meeting point' },
      { id: 'chat', title: 'Chat & app' },
    ],
  },
  {
    id: 'schedule',
    title: 'Schedule & departures',
    groups: [{ id: 'dates' }, { id: 'departures', title: 'Departures' }],
  },
  {
    id: 'content',
    title: 'Content',
    groups: [
      ...CONTEXT_GROUPS.map((g) => ({ id: g.id, title: g.title })),
      { id: 'review', title: 'Review & photo sharing' },
      { id: 'crosssells', title: 'Cross-sells' },
    ],
  },
  {
    id: 'guest',
    title: 'Guest journey',
    groups: [
      { id: 'selfreg', title: 'Self-registration' },
      { id: 'precheck', title: 'Pre-check-in' },
      { id: 'waiver', title: 'Waiver texts (notifications)' },
      { id: 'pretrip', title: 'Pre-trip items' },
    ],
  },
];

export const ITINERARY_SECTION: TourSectionDef = { id: 'itinerary', title: 'Line set', groups: [{ id: 'lineset' }] };
/** A TOUR-type tour's Itinerary tab: the workspace (`itinerary/ItineraryWorkspace`) places
 *  the days', stops' and activity rows' fields itself — outline and inspector. */
export const ITINERARY_WORKSPACE_SECTION: TourSectionDef = { id: 'itinerary', title: 'Itinerary', groups: [{ id: 'workspace' }] };

const E: FieldBlockKind = 'editable';
const S: FieldBlockKind = 'system';

function at(section: TourSectionId, group: string, order: number, block: FieldBlockKind = E): TourRegistryEntry {
  return { section, group, block, order };
}

const fixedDate: TourRegistryEntry['when'] = (d) => d.is_bespoke;
const recurring: TourRegistryEntry['when'] = (d) => !d.is_bespoke;

export const TOUR_RECORD_REGISTRY: Readonly<Record<string, TourRegistryEntry>> = {
  // ---- Launch & mapping: the identifiers table (the primary row, then one row per
  //      alias), the policy, access and system tags, the tag-categories table.
  custom_id: at('launch', 'mapping', 10),
  'additional_custom_ids.order': at('launch', 'mapping', 20),
  ...Object.fromEntries(
    ALIAS_FIELDS_CATALOG.map((f, i) => [
      f.key,
      f.key === 'alias.restech_account'
        ? { ...at('launch', 'mapping', 30 + i), via: 'alias.integration' }
        : at('launch', 'mapping', 30 + i),
    ]),
  ),
  launch_policy: at('launch', 'policy', 10),
  atag: at('launch', 'access', 10),
  tags: at('launch', 'access', 20),
  use_operator_defaults: at('launch', 'tag-categories', 10),
  'tag_categories.order': at('launch', 'tag-categories', 20),
  ...Object.fromEntries(TAGCAT_FIELDS_CATALOG.map((f, i) => [f.key, at('launch', 'tag-categories', 30 + i)])),

  // ---- Basics
  name: at('basics', 'basics', 10),
  internal_name: at('basics', 'basics', 15),
  description: at('basics', 'basics', 20),
  start_timezone: at('basics', 'basics', 30),
  use_as_template: at('basics', 'basics', 40),
  cover_image: at('basics', 'basics', 50),
  'meeting_point.name': at('basics', 'meeting', 10),
  'meeting_point.address': at('basics', 'meeting', 20),
  'meeting_point.latitude': at('basics', 'meeting', 30),
  'meeting_point.longitude': { ...at('basics', 'meeting', 31), via: 'meeting_point.latitude' },
  'meeting_point.maps_url': at('basics', 'meeting', 40),
  'meeting_point.comment': at('basics', 'meeting', 50),
  'meeting_point.photo': at('basics', 'meeting', 60),
  location_sharing: at('basics', 'chat', 10),
  mytrips_display: at('basics', 'chat', 20),
  has_group_chat: at('basics', 'chat', 30),
  exclude_from_chat: at('basics', 'chat', 40),
  disable_internal_chat: at('basics', 'chat', 50),
  show_all_users_to_guides_in_nonbespoke_tours: at('basics', 'chat', 60),
  chat_priority: at('basics', 'chat', 90, S),
  effective_display_currency: at('basics', 'chat', 95, S),

  // ---- Schedule & departures
  start_date: { ...at('schedule', 'dates', 10), when: fixedDate },
  length: at('schedule', 'dates', 20),
  length_is_manual: { ...at('schedule', 'dates', 21), via: 'length' },
  // Recomputed from the itinerary's days when the start date moves — a TOUR-type tour's
  // only (a bus trip / exhibition has no itinerary to derive it from).
  end_date: { ...at('schedule', 'dates', 30, S), when: (d) => d.is_bespoke && d.trip_type === 0 },
  departures: { ...at('schedule', 'departures', 10), when: recurring },

  // ---- Content
  ...Object.fromEntries(
    CONTEXT_GROUPS.flatMap((g) => g.keys.map((k, i) => [contextPath(k), at('content', g.id, (i + 1) * 10)])),
  ),
  'review.name': at('content', 'review', 10),
  'review.url': at('content', 'review', 20),
  'photo_sharing.name': at('content', 'review', 30),
  'photo_sharing.url': at('content', 'review', 40),
  positive_review_target: at('content', 'review', 50),
  positive_review_url: at('content', 'review', 60),
  crosssells: at('content', 'crosssells', 10),

  // ---- Guest journey
  allow_selfreg: at('guest', 'selfreg', 10),
  selfreg_verify_email: at('guest', 'selfreg', 20),
  selfreg_verify_phone: at('guest', 'selfreg', 30),
  use_operator_pre_check_settings: at('guest', 'precheck', 10),
  // A scope's question switch rides on its categories' slot (one control: the questions
  // are entries of the field list — `precheck.ts`).
  ...Object.fromEntries(
    PRECHECK_FIELDS_CATALOG.map((f, i) => {
      const host = f.key.endsWith('_tag_categories_enabled') ? f.key.replace('_tag_categories_enabled', '_selected_tag_categories') : null;
      return [f.key, host ? { ...at('guest', 'precheck', 20 + i), via: host } : at('guest', 'precheck', 20 + i)];
    }),
  ),
  ...Object.fromEntries(WAIVER_CONTEXT_KEYS.map((k, i) => [contextPath(k), at('guest', 'waiver', (i + 1) * 10)])),
  'pre_trip_items.order': at('guest', 'pretrip', 10),
  ...Object.fromEntries(PRETRIP_FIELDS_CATALOG.map((f, i) => [f.key, at('guest', 'pretrip', 20 + i)])),

  // ---- Itinerary tab
  vehicle_route_set: at('itinerary', 'lineset', 10),
  ...Object.fromEntries(ITINERARY_FIELDS_CATALOG.map((f, i) => [f.key, at('itinerary', 'workspace', 10 + i)])),
};

/** The keys a section's group shows, in order (`via` keys ride on another slot). With a
 *  tour: only the keys shown for it (`when`). */
export function groupKeys(
  section: TourSectionId,
  group: string,
  block: FieldBlockKind = E,
  detail?: Pick<TourDetail, 'is_bespoke' | 'trip_type'>,
): string[] {
  return Object.entries(TOUR_RECORD_REGISTRY)
    .filter(([, e]) => e.section === section && e.group === group && e.block === block && !e.via)
    .filter(([, e]) => !detail || !e.when || e.when(detail))
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([k]) => k);
}

/** The section a catalog key sits in (null: not placed). A `via` key answers with its
 *  host's section. */
export function sectionOfKey(key: string): TourSectionId | null {
  return TOUR_RECORD_REGISTRY[key]?.section ?? null;
}

/** The DOM id of a section's panel — the `tour#<section>` anchor. */
export function sectionAnchorId(section: TourSectionId): string {
  return `tour-section-${section}`;
}

/** The blocks inside the panels that the launch band's nodes (`1`–`6`) and `tour#<block>`
 *  links land on: the Launch & mapping blocks, Basics' Chat & app group, the departures,
 *  and the Guest journey's blocks (the old Departures / Pre-trip items / Pre-check-in tab
 *  slugs land on theirs). */
export const TOUR_BLOCKS = [
  'mapping',
  'import',
  'policy',
  'access',
  'tag-categories',
  'readiness',
  'guest-app',
  'departures',
  'selfreg',
  'pre-check-in',
  'pre-trip-items',
] as const;
export type TourBlockId = (typeof TOUR_BLOCKS)[number];

/** The DOM id of a block — the `tour#<block>` anchor. */
export function blockAnchorId(block: TourBlockId): string {
  return `tour-block-${block}`;
}

/** The element a `tour#<hash>` names: a section's panel or a block inside one. */
export function anchorIdOfHash(hash: string): string | null {
  if (TOUR_SECTIONS.some((s) => s.id === hash)) return sectionAnchorId(hash as TourSectionId);
  if ((TOUR_BLOCKS as readonly string[]).includes(hash)) return blockAnchorId(hash as TourBlockId);
  return null;
}
